import { type Ref, shallowRef } from 'vue';
import { initializeForVisitor } from '@/app/i18n';
import { fetchShared } from '@/app/query-client';
import { router } from '@/app/router';
import { analytics } from '@/services/Analytics';
import { portalImagesQuery } from '@/services/api/endpoints/customer_download';
import { httpStatusOf } from '@/services/api/http';
import { kioskConfig } from '@/services/KioskConfig';
import { kioskDesign } from '@/services/KioskDesign';
import { session } from '@/services/Session';
import { captureUpsell } from '@/services/telemetry/posthog';
import { sentryService } from '@/services/telemetry/sentry';
import type { CustomerPortalSessionResponse } from '@/shared/types/api';
import type {
  CustomerDownloadPageStatus,
  CustomerPortraitDownloadItem,
} from './customerPortraitDownloadTypes';
import type { CustomerDigitalExtrasState } from './useCustomerDigitalExtras';
import type { CustomerPortraitGalleryState } from './useCustomerPortraitGalleryState';

type CustomerDownloadCodePageGalleryState = Pick<
  CustomerPortraitGalleryState,
  | 'getErrorRate'
  | 'imageErrorCount'
  | 'imageLoadCount'
  | 'initializePortraits'
  | 'isInternalApiUrl'
  | 'setGalleryContext'
>;

interface UseCustomerDownloadCodePageOptions {
  digitalExtras: Pick<CustomerDigitalExtrasState, 'load' | 'stop'>;
  galleryState: CustomerDownloadCodePageGalleryState;
  isUnmounted: Ref<boolean>;
  loadStartTime: Ref<number | null>;
}

export interface CustomerDownloadCodePage {
  brandingWarning: Ref<boolean>;
  getAbortSignal: () => AbortSignal | undefined;
  getDownloadCodeFromRouter: () => string;
  isGreenscreen: Ref<boolean>;
  mount: () => Promise<void>;
  retry: () => Promise<void>;
  sessionPass: Ref<CustomerSessionPassData | null>;
  status: Ref<CustomerDownloadPageStatus>;
  unmount: () => void;
}

export interface CustomerSessionPassData {
  baseTheme?: string;
  kioskLocation?: string;
  kioskName?: string;
  styleProfile?: unknown;
  ticketCode: string;
}

type PortraitsOutcome = 'loaded' | 'processing' | 'empty';

/**
 * Which screen a session with no portraits should show. A green-screen visitor
 * holding a ticket code needs the pass and its QR whichever way the backend
 * phrased it — they have not reached the buying station yet.
 */
function statusWithoutPortraits(
  outcome: Exclude<PortraitsOutcome, 'loaded'>,
  hasSessionPass: boolean
): CustomerDownloadPageStatus {
  if (hasSessionPass) {
    return 'pass-only';
  }
  return outcome === 'processing' ? 'processing' : 'empty';
}

type LegacyAnalyticsMetricMethod =
  | typeof analytics.trackBoolean
  | typeof analytics.trackTimeTo;

type LoadPhase = 'portraits' | 'session';

function callAnalyticsMetricWithOldArity(
  method: LegacyAnalyticsMetricMethod,
  metric: string
): void {
  Reflect.apply(method, analytics, [metric]);
}

function errorMessage(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return undefined;
}

// Only reached for a real fault now: a rejected request, or a session step
// that failed. The no-portraits states never come through here — see
// PortraitsOutcome.
function pageStatusForError(error: unknown): CustomerDownloadPageStatus {
  const status = httpStatusOf(error);
  if (status === 404) {
    return 'not-found';
  }
  if (status === 202) {
    // Kept for a client that rejects on 202 rather than resolving it.
    return 'processing';
  }
  return 'server-error';
}

function sessionPassFromSessionData(
  sessionData: CustomerPortalSessionResponse
): CustomerSessionPassData | null {
  if (!(sessionData.is_greenscreen && sessionData.ticket_code?.trim())) {
    return null;
  }
  return {
    ticketCode: sessionData.ticket_code,
    ...(sessionData.kiosk_name ? { kioskName: sessionData.kiosk_name } : {}),
    ...(sessionData.kiosk_location
      ? { kioskLocation: sessionData.kiosk_location }
      : {}),
    ...(sessionData.base_theme ? { baseTheme: sessionData.base_theme } : {}),
    ...(sessionData.style_profile
      ? { styleProfile: sessionData.style_profile }
      : {}),
  };
}

// The photos never wait long for the extras list: when it is slower than this,
// the page renders without the video tab and adds it once the list arrives.
const EXTRAS_GRACE_MS = 2000;

function settleWithin(promise: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const grace = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, grace]).finally(() => clearTimeout(timer));
}

function startSpan(name: string, operation: string) {
  try {
    return sentryService.startSpan(name, operation);
  } catch {
    return null;
  }
}

export function useCustomerDownloadCodePage(
  options: UseCustomerDownloadCodePageOptions
): CustomerDownloadCodePage {
  const abortController = shallowRef<AbortController | null>(null);
  const brandingWarning = shallowRef(false);
  const sessionPass = shallowRef<CustomerSessionPassData | null>(null);
  const isGreenscreen = shallowRef(false);
  const status = shallowRef<CustomerDownloadPageStatus>('loading');
  let loadingPromise: Promise<void> | null = null;

  function getAbortSignal(): AbortSignal | undefined {
    return abortController.value?.signal;
  }

  function getDownloadCodeFromRouter(): string {
    const downloadCode = router.route.params.downloadCode as string | undefined;
    return (downloadCode || '').toLowerCase().replace(/[^0-9a-z]/gi, '');
  }

  async function initializeSessionAndKiosk(
    downloadCode: string
  ): Promise<void> {
    sentryService.addBreadcrumb('session', 'Initializing session', {
      download_code_length: downloadCode.length,
    });

    const loadSpan = startSpan('download.session_init', 'function');

    try {
      const sessionData: CustomerPortalSessionResponse =
        await session.continueFromDownloadCode(downloadCode, {
          captureError: false,
          showError: false,
        });

      if (options.isUnmounted.value) {
        return;
      }

      sentryService.addBreadcrumb('session', 'Session continued successfully', {
        source: sessionData?.source,
      });

      isGreenscreen.value = Boolean(sessionData.is_greenscreen);
      if (sessionData.is_greenscreen) {
        captureUpsell('extras_download_page_viewed', sessionData.session_id, {
          skin: sessionData.base_theme,
          location: sessionData.kiosk_location,
        });
        sessionPass.value = sessionPassFromSessionData(sessionData);
        kioskDesign.kiosk = {
          slug: 'greenscreen',
          client_name: 'Green Screen Photo Booth',
          dark_mode: true,
          background_color: '#000000',
          button_background_color: '#5e3fb6',
          button_font_color: '#FFFFFF',
          font_color: '#FFFFFF',
          languages: ['en'],
        } as unknown as typeof kioskDesign.kiosk;
        return;
      }

      try {
        await kioskConfig.loadBranding(sessionData.source);
        if (!options.isUnmounted.value) {
          initializeForVisitor(kioskDesign.kiosk);
          sentryService.addBreadcrumb('kiosk', 'Kiosk design initialized');
        }
      } catch (kioskError) {
        brandingWarning.value = true;
        sentryService.captureException(kioskError, {
          tags: {
            component: 'CustomerPortraitsDownloadCode',
            operation: 'kiosk_init',
          },
        });
      }
    } catch (sessionError) {
      if (httpStatusOf(sessionError) !== 404) {
        sentryService.captureException(sessionError, {
          tags: {
            component: 'CustomerPortraitsDownloadCode',
            operation: 'session_continue',
          },
        });
      }
      throw sessionError;
    } finally {
      try {
        loadSpan?.end();
      } catch {
        // Span cleanup must not block page initialization.
      }
    }
  }

  async function fetchPortraits(
    downloadCode: string
  ): Promise<PortraitsOutcome> {
    const fetchSpan = startSpan('download.fetch_portraits', 'http.client');

    sentryService.addBreadcrumb('api', 'Fetching portraits', {
      download_code_length: downloadCode.length,
    });

    try {
      const response = await fetchShared(portalImagesQuery(downloadCode));

      if (options.isUnmounted.value) {
        // The caller re-checks isUnmounted before reading this.
        return 'loaded';
      }

      const portraitsList: CustomerPortraitDownloadItem[] = (
        response.images ?? []
      ).map((image) => ({ ...image }));

      if (portraitsList.length === 0) {
        // 202 is a 2xx, so the client resolves it and the body is the only
        // signal that the composite is still being produced.
        return response.processing ? 'processing' : 'empty';
      }

      sentryService.addBreadcrumb('api', 'Portraits fetched successfully', {
        count: portraitsList.length,
        has_external_urls: portraitsList.some(
          (portrait) =>
            portrait.url && !options.galleryState.isInternalApiUrl(portrait.url)
        ),
      });

      options.galleryState.initializePortraits(portraitsList);
      return 'loaded';
    } catch (error) {
      // Every expected state returned above, so anything here is a real fault
      // and belongs in Sentry. No allowlist of sentinel messages to keep in
      // sync — that is what let KIOSK-FRONTEND-15B/15C open an issue, and a
      // replay, for each visitor at the most ordinary moment of the flow.
      if (![202, 404].includes(httpStatusOf(error) ?? 0)) {
        sentryService.captureException(error, {
          tags: {
            component: 'CustomerPortraitsDownloadCode',
            operation: 'fetch_portraits',
          },
        });
      }
      throw error;
    } finally {
      try {
        fetchSpan?.end();
      } catch {
        // Span cleanup must not block portrait loading.
      }
    }
  }

  async function load(): Promise<void> {
    status.value = 'loading';
    brandingWarning.value = false;
    sessionPass.value = null;
    options.digitalExtras.stop();
    isGreenscreen.value = false;
    options.loadStartTime.value = Date.now();

    abortController.value?.abort();
    abortController.value = new AbortController();

    const downloadCode = getDownloadCodeFromRouter();
    if (!downloadCode) {
      router.redirectToPath('/customer/portraits-download');
      return;
    }
    if (!/^[a-z0-9]{8}$/.test(downloadCode)) {
      status.value = 'invalid';
      return;
    }

    try {
      analytics.setCustomerDownloadCode(downloadCode);
    } catch {
      // Analytics setup is non-critical.
    }

    let phase: LoadPhase = 'session';
    try {
      await initializeSessionAndKiosk(downloadCode);
      if (options.isUnmounted.value) {
        return;
      }

      phase = 'portraits';
      const extrasLoaded = sessionPass.value
        ? options.digitalExtras.load(downloadCode)
        : null;
      const outcome = await fetchPortraits(downloadCode);
      if (options.isUnmounted.value) {
        return;
      }
      if (extrasLoaded) {
        await settleWithin(extrasLoaded, EXTRAS_GRACE_MS);
        if (options.isUnmounted.value) {
          return;
        }
      }

      if (outcome !== 'loaded') {
        // Not a failure, so it does not go through the catch: no exception, no
        // Sentry issue, no replay. A breadcrumb keeps it visible when a later
        // error on this page needs the context.
        status.value = statusWithoutPortraits(
          outcome,
          sessionPass.value !== null
        );
        sentryService.addBreadcrumb(
          'api',
          'Session carries no portraits yet',
          { outcome, status: status.value },
          'info'
        );
        return;
      }

      status.value = 'ready';
      try {
        callAnalyticsMetricWithOldArity(
          analytics.trackBoolean,
          analytics.METRICS.download_engaged
        );
        callAnalyticsMetricWithOldArity(
          analytics.trackTimeTo,
          analytics.METRICS.time_download_initiated
        );
      } catch {
        // Analytics failure must not change the ready state.
      }
      options.galleryState.setGalleryContext();
    } catch (error) {
      if (!options.isUnmounted.value) {
        status.value = pageStatusForError(error);
      }
      sentryService.addBreadcrumb(
        'error',
        'Download page initialization failed',
        {
          error: errorMessage(error),
          phase,
          status: status.value,
        },
        httpStatusOf(error) === 404 ? 'warning' : 'error'
      );
    }
  }

  async function runLoad(): Promise<void> {
    if (loadingPromise) {
      return loadingPromise;
    }
    loadingPromise = load().finally(() => {
      loadingPromise = null;
    });
    return loadingPromise;
  }

  async function mount(): Promise<void> {
    options.isUnmounted.value = false;

    initializeForVisitor();

    await runLoad();
  }

  async function retry(): Promise<void> {
    await runLoad();
  }

  function unmount(): void {
    options.isUnmounted.value = true;
    options.digitalExtras.stop();

    try {
      analytics.setCustomerDownloadCode('');
    } catch {
      // Analytics cleanup must not block component unmount.
    }

    abortController.value?.abort();
    abortController.value = null;

    try {
      if (options.loadStartTime.value) {
        sentryService.trackMetric(
          'download.total_page_time_ms',
          Date.now() - options.loadStartTime.value,
          'millisecond'
        );
      }
      sentryService.trackMetric(
        'download.error_rate',
        options.galleryState.getErrorRate(),
        'ratio'
      );
      sentryService.addBreadcrumb('navigation', 'Download page unmounted', {
        images_loaded: options.galleryState.imageLoadCount.value,
        images_failed: options.galleryState.imageErrorCount.value,
      });
    } catch {
      // Telemetry failure must not block component unmount.
    }
  }

  return {
    brandingWarning,
    getAbortSignal,
    getDownloadCodeFromRouter,
    isGreenscreen,
    mount,
    retry,
    sessionPass,
    status,
    unmount,
  };
}
